<template>
  <div class="pb-6 mb-6">
    <gat-dialog
      v-model="dialogVisible"
      :cancel-button-disabled="isSaving"
      cancelButtonCaption="CANCEL"
      okButtonCaption="SAVE"
      width="1000"
      :auto-close="false"
      @okClick="addOrEditProspect()"
      @cancelClick="close(true)"
      :ok-button-disabled="!isValid || !canSave">
      <span class="text-h6 grey--text text--darken-1"
        >{{ this.selectedProspect?.CHO_ID ? 'Edit' : 'Add' }} prospect</span
      >
      <gat-form title="Prospect detail" :is-loading="isSaving || loading" ref="form" v-model="isValid">
        <gat-sub-group size="xs12 sm12">
          <GatEdit label="Name" v-model="prospectName" size="xs6" :maxChars="100" required />
          <GatEdit label="Email subject" v-model="prospectEmailSubject" size="xs6" :maxChars="500" />
        </gat-sub-group>
        <gat-grid
          title="Cargo lines"
          class="elevation-0"
          :items="cargoItems"
          :columns="cargoColumns"
          :hideEmptyColumns="true"
          selectable
          keyName="ID"
          max-height="40vh"
          no-footer
          :selectedKeys="prospectCargoLines"
          @selectionChanged="cargoGridSelectionCanged($event)">
        </gat-grid>
        <gat-grid
          title="Clients"
          class="elevation-0"
          :items="clientsContactsItems"
          :columns="clientsColumns"
          max-height="40vh"
          :hideEmptyColumns="true"
          selectable
          keyName="SELECTION_KEY"
          no-footer
          :selectedKeys="prospectConnectedClients"
          @selectionChanged="clientsGridSelectionCanged($event)">
          <template slot="btns">
            <GatEdit label="Hide contacts" v-model="hideContacts" toggle size="xs12" />
          </template>
        </gat-grid>
      </gat-form>
    </gat-dialog>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-cycle
import { apiGet, apiPut } from '@/store/api';
import { usePortcallStore } from '@/store/portcallStore';
import { useApplicationStore } from '@/store/applicationStore';

export default {
  name: 'ProspectItem',
  props: {
    showForm: Boolean,
    clientsContacts: {
      type: Array,
      required: true,
    },
    callId: {
      type: [String, Number],
      required: true,
    },
    selectedProspect: {
      type: [Object, null, undefined],
      required: false,
    },
  },
  components: {},
  setup() {
    const portcallStore = usePortcallStore();
    const applicationStore = useApplicationStore();
    return {
      portcallStore,
      applicationStore,
    };
  },
  data() {
    return {
      loading: false,
      isValid: false,
      isSaving: false,
      hideContacts: false,
      prospectCargoLines: [],
      prospectConnectedClients: [],
      includedClientKeys: [],
      dialogVisible: this.showForm,
      prospectName: this.selectedProspect?.CHO_NAME ?? '',
      prospectEmailSubject: this.selectedProspect?.CHO_SUBJECT ?? '',
    };
  },

  created() {},

  mounted() {
    this.loading = false;
    this.isSaving = false;
    if (this.selectedProspect?.CHO_ID) {
      try {
        this.loading = true;
        apiGet(`prospects/${this.selectedProspect.CHO_ID}/items`).then((hotlistItems) => {
          this.cargoGridSelectionCanged(hotlistItems.filter((hi) => hi.CHI_CARGO_ID).map((ci) => ci.CHI_CARGO_ID));
          const clientsContacts = hotlistItems.filter((hi) => !hi.CHI_CARGO_ID);
          const cContacts = clientsContacts.map((c) => {
            let type = 1;
            if (c.CHI_HOTLIST_PROV_ID) {
              type = 3;
            } else if (c.CHI_CONTACT_ID) {
              type = 2;
            }
            const id = c.CHI_HOTLIST_PROV_ID || c.CHI_CONTACT_ID || c.CHI_CLIENT_ID;
            const roleId = c.CHI_CLIENT_ROLE > 0 ? c.CHI_CLIENT_ROLE : null;
            return this.clientContactRows.find(
              (contact) =>
                contact.TYPE === type &&
                contact.ID === id &&
                (type === 3 || (contact.CLIENT_ROLE_ID > 0 ? contact.CLIENT_ROLE_ID : null) === roleId)
            )?.SELECTION_KEY;
          });
          this.includedClientKeys = cContacts.filter((key) => key != null);
          this.clientsGridSelectionCanged(cContacts);
          this.loading = false;
        });
        this.prospectName = this.selectedProspect.CHO_NAME;
        this.prospectEmailSubject = this.selectedProspect.CHO_SUBJECT;
      } catch {
        this.loading = false;
      }
    } else if (this.callId) {
      this.prospectName = `Prospects ${this.portcallStore.callDetails.vessel.NAME}`;
      apiGet(`portcall/${this.callId}/harbour`).then((result) => {
        this.prospectEmailSubject = `Prospects ${this.portcallStore.callDetails.vessel.NAME} - ${result?.NAME ?? ''}`;
      });
    }
  },

  watch: {
    showForm(newValue) {
      this.dialogVisible = newValue;
    },
  },

  computed: {
    cargoItems() {
      return this.portcallStore.cargo;
    },
    clientContactRows() {
      const rows = new Map();
      this.clientsContacts.forEach((contact) => {
        if (contact.ID != null) {
          const rowType = Number(contact.RowType ?? contact.TYPE);
          const roleId = rowType !== 3 && contact.CLIENT_ROLE_ID > 0 ? contact.CLIENT_ROLE_ID : '';
          const key = `${rowType}:${contact.ID}:${roleId}`;
          if (!rows.has(key)) rows.set(key, { ...contact, TYPE: rowType, RowType: rowType, SELECTION_KEY: key });
        }
      });
      return [...rows.values()];
    },
    clientsContactsItems() {
      return this.clientContactRows.filter((contact) => {
        if (contact.RowType === 1) return true;
        if (this.includedClientKeys.includes(contact.SELECTION_KEY)) return true;
        if (contact.RowType === 2) return !this.hideContacts && Number(contact.data?.INACTIVE ?? 0) === 0;
        if (contact.RowType === 3) return (contact.EMAIL ?? '').trim() !== '';
        return true;
      });
    },
    canCreateNewProspect() {
      return !this.isSaving && this.applicationStore.userRight('PRO.NEW') && this.applicationStore.user.internal;
    },
    canDeleteProspects() {
      return !this.isSaving && this.applicationStore.userRight('PRO.DELETE') && this.applicationStore.user.internal;
    },
    canSave() {
      return this.prospectConnectedClients.length > 0 && this.canCreateNewProspect;
    },
    cargoColumns() {
      const result = [];
      result.push({ header: 'Cargo', field: 'BL_TEXT', hideOnPhone: false });
      result.push({ header: 'Gross weight', field: 'AMOUNT_GROSS_WEIGHT', hideOnPhone: false });
      result.push({ header: 'Net weight', field: 'AMOUNT_WEIGHT', hideOnPhone: false });
      result.push({ header: 'Code', field: 'CODE', hideOnPhone: false });
      result.push({ header: 'Quay', field: 'QUAYNAME', hideOnPhone: false });
      return result;
    },
    clientsColumns() {
      const result = [];
      // result.push({ header: 'Id', field: 'ID', hideOnPhone: true });
      result.push({ header: 'Role', field: 'ROLE', hideOnPhone: false });
      result.push({ header: 'Client name', field: 'NAME', hideOnPhone: false });
      result.push({ header: 'Email', field: 'EMAIL', hideOnPhone: false });
      result.push({ header: 'Type', field: 'RowType', hideOnPhone: true, hide: true });
      // result.push({ header: 'Role id', field: 'CLIENT_ROLE_ID', hideOnPhone: true });
      return result;
    },
  },

  methods: {
    clientsGridSelectionCanged(items) {
      const keys = [...new Set(items)].filter((key) => this.clientContactRows.some((row) => row.SELECTION_KEY === key));
      if (
        keys.length !== this.prospectConnectedClients.length ||
        keys.some((key) => !this.prospectConnectedClients.includes(key))
      ) {
        this.prospectConnectedClients = keys;
      }
    },
    cargoGridSelectionCanged(items) {
      const keys = [...new Set(items)].filter((key) => key != null);
      if (
        keys.length !== this.prospectCargoLines.length ||
        keys.some((key) => !this.prospectCargoLines.includes(key))
      ) {
        this.prospectCargoLines = keys;
      }
    },
    async addOrEditProspect() {
      try {
        this.$refs.form.validate();
        if (this.isValid && this.canSave) {
          this.isSaving = true;
          const hotlistItems = [];
          this.prospectCargoLines.forEach((cId) => {
            const cargo = this.cargoItems.find((cg) => cg.ID === cId);
            hotlistItems.push({
              CHI_CLIENT_ID: null,
              CHI_CARGO_ID: cargo.ID,
              CHI_CLIENT_ROLE: null,
              CHI_HOTLIST_PROV_ID: null,
              CHI_CONTACT_ID: null,
            });
          });
          this.prospectConnectedClients.forEach((key) => {
            const clientContact = this.clientContactRows.find((cc) => cc.SELECTION_KEY === key);
            if (clientContact) {
              const roleId = clientContact.CLIENT_ROLE_ID > 0 ? clientContact.CLIENT_ROLE_ID : null;
              if (clientContact.TYPE === 3) {
                hotlistItems.push({
                  CHI_CLIENT_ID: null,
                  CHI_CARGO_ID: null,
                  CHI_CLIENT_ROLE: null,
                  CHI_HOTLIST_PROV_ID: clientContact.ID,
                  CHI_CONTACT_ID: null,
                });
              } else if (clientContact.TYPE === 2) {
                hotlistItems.push({
                  CHI_CLIENT_ID: null,
                  CHI_CARGO_ID: null,
                  CHI_CLIENT_ROLE: roleId,
                  CHI_HOTLIST_PROV_ID: null,
                  CHI_CONTACT_ID: clientContact.ID,
                });
              } else if (clientContact.TYPE === 1) {
                hotlistItems.push({
                  CHI_CLIENT_ID: clientContact.ID,
                  CHI_CARGO_ID: null,
                  CHI_CLIENT_ROLE: roleId,
                  CHI_HOTLIST_PROV_ID: null,
                  CHI_CONTACT_ID: null,
                });
              }
            }
          });
          const newProspect = {
            CHO_ID: this.selectedProspect?.CHO_ID, // existing prospect to edit
            HOTLIST: {
              CHO_PORTCALL_ID: this.callId,
              CHO_NAME: this.prospectName,
              CHO_SUBJECT: this.prospectEmailSubject,
            },
            HOTLIST_ITEMS: hotlistItems,
          };
          await apiPut('prospects', { value: newProspect });
          this.isSaving = false;
          this.close();
        }
      } catch {
        this.isSaving = false;
        this.close(true);
      } finally {
        this.isSaving = false;
        this.close();
      }
    },
    close(cancel) {
      this.isSaving = false;
      this.$emit('close', cancel);
    },
  },
};
</script>

<style lang="scss" scoped></style>
